import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import asyncDispatch from '../../../../../services/mini/asyncDispatch';
import * as projectActions from '../../../../../redux/projects/actions.projects';
import * as unloadPageActions from '../../../../../redux/unloadPage/actions.unloadPage';
import { injectStripe } from 'react-stripe-elements';
import TextFieldWrapper from '../../../../components/TextFieldWrapper/TextFieldWrapper';
import capitalize from '../../../../../services/mini/capitalize';
import {Loader} from '../../../../components/Loader/Loader';
import getErrorMsg from '../../../../../services/mini/getErrorMsg';
import { BILLING_STRIPE } from '../../../../../const/billingTypes';
import billingEvents from '../../../../../const/billingEvents';
import ToasterService from '../../../../../services/ToasterService';
import Money from '../../../../components/Money/Money';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import PaymentCardInfo from '../../PaymentCardInfo/PaymentCardInfo';
import StripeSecureInfo from '../../StripeSecureInfo/StripeSecureInfo';

class SavedCreditCardsForm extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            amount: this.props.amount || 1,
            amountFieldError: null,
            amountFieldInfo: null,
            payingIsInProcess: false,
            cardLabelVisible: false,
            selectedCardId: _.find(props.cards, { isActive: true })?.paymentMethodId
        };

        this.maxFieldLength = 500;
    }

    getTrackInfo = (status, amount = 0) => {
        const {
            isInitialBilling,
            contentId,
            contentVersion
        } = this.context;

        return {
            paymentMethod: BILLING_STRIPE,
            isInitialBilling: isInitialBilling,
            contentId,
            contentVersion,
            paymentStatus: status,
            paymentAmount: amount
        };
    }

    trackPayment = (status, amount) => {
        const {
            trackPaymentStart = _.noop(),
        } = this.context;

        trackPaymentStart(this.getTrackInfo(status, amount));
    }

    submit = async () => {
        const {
            getStripePaymentIntent,
            proceedStripePayment,
            refund,
            onBillingFinishedCb
        } = this.props;

        const {
            amount,
            selectedCardId
        } = this.state;

        const {
            trackPaymentError = _.noop(),
            trackPaymentSuccess = _.noop(),
        } = this.context;

        this.setState({ payingIsInProcess: true });
        if (!this.validate()) {
            this.setState({ payingIsInProcess: false });
            this.trackPayment(billingEvents.clientValidationFailed);
            return;
        }

        this.trackPayment(billingEvents.paymentBtnClicked, +amount);

        getStripePaymentIntent({ amount: +amount * 100 }).then(async (clientSecret) => {
            await this.props.stripe.confirmCardPayment(clientSecret, {
                payment_method: selectedCardId,
                setup_future_usage: 'off_session'
            }).then((result) => {
                if (result.error) {
                    ToasterService.showError({ title: result.error.message });
                    this.setState({ payingIsInProcess: false });
                    trackPaymentError(this.getTrackInfo(billingEvents.getStripePaymentIntentError + getErrorMsg(result.error)));
                } else {
                    if (result.paymentIntent.status === 'succeeded') {
                        proceedStripePayment({
                            stripePaymentIntentId: result.paymentIntent.id,
                            refund: refund
                        }).then(() => {
                            trackPaymentSuccess({ ...this.getTrackInfo(), paymentAmount: +amount });
                            onBillingFinishedCb(result.paymentIntent.id);
                        }, (chargeUserErr) => {
                            this.setState({ payingIsInProcess: false });
                            trackPaymentError(this.getTrackInfo(billingEvents.proceedStripePaymentError + getErrorMsg(chargeUserErr)));
                        });
                    }
                }
            });
        });
    }

    validate = (validationType) => {
        let amountFieldError = null;
        let amountFieldInfo = null;

        if (validationType === 'amount') {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength(validationType);
        }
        else {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength('amount');
        }

        this.setState({
            amountFieldError,
            amountFieldInfo,
        });

        return amountFieldError === null &&
            amountFieldInfo === null;
    }

    validateAmount = () => {
        let { amount } = this.state;

        amount = +amount;

        if (isNaN(amount))
            return 'Amount is not valid';

        if (amount <= 0) {
            return 'Min amount is $1';
        }

        if (amount > 9999) {
            return 'Max amount is $9999';
        }
        return null;
    }

    validateField = (field) => {
        const fieldVal = this.state[field];
        let fieldError = null;

        if (!fieldVal) {
            if (field === 'name') {
                fieldError = 'Name on card should not be empty';
            } else {
                fieldError = capitalize(`${field} should not be empty`);
            }
        }

        return fieldError;
    }

    validateFieldLength = (fieldType) => {
        const field = this.state[fieldType];
        let fieldInfo = null,
            type;
        if (field.length > this.maxFieldLength) {
            if (!this.state[fieldType + 'FieldLength']) {
                this.setState({ [fieldType + 'FieldLength']: field.length });
            }
            if (fieldType === 'name') {
                type = 'name';
            }
            return `Max ${type} length: ${this.maxFieldLength} characters`;
        }

        this.setState({ [fieldType + 'FieldLength']: null });

        return fieldInfo;
    }

    onLengthValidate = () => {
        this.validate();
    }

    onTextInputChange = (e) => {
        const field = e.target.name;
        const fieldValue = e.target.value;
        const re = /^[0-9\b]+$/;
        let newState = this.state;

        if (field === 'amount') {
            if (fieldValue === '' || re.test(fieldValue)) {
                newState[field] = fieldValue;
            }
        } else {
            newState[field] = fieldValue;
        }

        this.setState(newState);
    }

    onTextInputBlur = (validationType) => {
        this.validate(validationType);
    }

    changeCard = ({ paymentMethodId }) => {
        this.setState({ selectedCardId: paymentMethodId });
    }

    render() {
        const {
            amount,
            payingIsInProcess,
            amountFieldError,
            amountFieldInfo,
            selectedCardId
        } = this.state;

        const {
            readOnlyAmount,
            cards
        } = this.props;

        return (
            <div className="saved-credit-cards-form">
                {payingIsInProcess && <Loader theme="dark" size="medium" center={true} appearWithFade={true} />}
                <div>
                    {
                        readOnlyAmount ?
                            <div className="billing-amount-readonly">
                                <Money amount={amount} />
                            </div> :
                            <TextFieldWrapper
                                placeholder="Your order"
                                label="Your order"
                                name="amount"
                                id="amount"
                                required={true}
                                className={`${payingIsInProcess && 'disabled'} text-field-with-left-margin`}
                                value={amount}
                                error={amountFieldError}
                                info={amountFieldInfo}
                                onLengthValidate={this.onLengthValidate}
                                onChange={this.onTextInputChange}
                                onBlur={this.onTextInputBlur} />
                    }

                    <ComboBox
                        values={cards}
                        valueKey="paymentMethodId"
                        valueName="name"
                        classes={`saved-credit-cards-form__cards-cb ${payingIsInProcess && 'disabled'}`}
                        selectedValueId={selectedCardId}
                        onValueSelect={this.changeCard}
                        showListForOneItem={false}
                        renderItem={(optionsItem, inList, closeComboBoxCb) => {
                            return <div key={optionsItem.paymentMethodId}
                                className="saved-credit-cards-form__cards-cb-item">
                                <PaymentCardInfo
                                    showCardName={true}
                                    {...optionsItem} />
                            </div>;
                        }} />
                    <div className="popup-form_btns">
                        <div onClick={this.submit}
                            className={`popup-form_btn action-btn big only-btn ${payingIsInProcess && 'disabled'}`}>
                            {'Submit Payment'}
                        </div>
                    </div>
                    <StripeSecureInfo disabled={payingIsInProcess} />
                </div>
            </div>
        );
    }
}

SavedCreditCardsForm.contextTypes = {
    trackPaymentStart: PropTypes.func,
    trackPaymentError: PropTypes.func,
    trackPaymentSuccess: PropTypes.func,
    isInitialBilling: PropTypes.bool,
    contentId: PropTypes.string,
    contentVersion: PropTypes.string,
};

function mapStateToProps({ user }, ownProps) {
    return {
        balance: user.balance,
        cards: user.cards,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        sendBillingToken: (param) => asyncDispatch(projectActions.sendBillingToken, dispatch, param),
        getStripePaymentIntent: (param) => asyncDispatch(projectActions.getStripePaymentIntent, dispatch, param),
        proceedStripePayment: (param) => asyncDispatch(projectActions.proceedStripePayment, dispatch, param),
        updateUnloadPageListenerForBilling: (param) => dispatch(unloadPageActions.updateUnloadPageListenerForBilling(param)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectStripe(SavedCreditCardsForm));
